import type {
  SmartCreationReviewEditChange,
  SmartCreationStrategyLane,
  SmartCreationStrategyOperation,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { toast } from "sonner";
import { isOptimisticReviewChange } from "@/components/smart-creation/optimistic-review";
import { getApiErrorMessage } from "@/lib/api-error";
import { updateSmartCreationReviewWorkspace } from "@/lib/smart-creation-api";
import type { PlanSession } from "./use-plan-session";
import type { WorkspaceMutations } from "./use-workspace-mutations";

interface PreviewCommandsOptions {
  session: PlanSession;
  mutations: WorkspaceMutations;
  confirmPreviewRegeneration: () => Promise<boolean>;
  goBack: () => Promise<void>;
}

/**
 * Commands of the Review Workspace (preview step) and the strategy edits that can be made
 * from it. Review commands first drain queued optimistic edits so they never race a save.
 */
export function usePreviewCommands({
  session,
  mutations,
  confirmPreviewRegeneration,
  goBack,
}: PreviewCommandsOptions) {
  const { browserDraftRef, currentIdentityRef, mountedRef, reviewQueueRef } = session;
  const { beginAction } = mutations;

  const applyReviewChanges = async (changes: SmartCreationReviewEditChange[]): Promise<boolean> => {
    const requestedIdentity = currentIdentityRef.current;
    if (
      !(await reviewQueueRef.current!.flush()) ||
      !mountedRef.current ||
      requestedIdentity !== currentIdentityRef.current
    )
      return false;
    const currentPlan = browserDraftRef.current;
    if (currentPlan?.status !== "preview_ready" || !currentPlan.reviewWorkspace) return false;
    try {
      const next = await updateSmartCreationReviewWorkspace(
        currentPlan.id,
        currentPlan.shopId,
        {
          expectedPlanRevision: currentPlan.revision,
          expectedWorkspaceRevision: currentPlan.reviewWorkspace.workspaceRevision,
          clientMutationId: crypto.randomUUID(),
          changes,
        },
        session.capabilitiesQuery.data,
      );
      if (mountedRef.current && requestedIdentity === currentIdentityRef.current)
        session.setPlan(next);
      return requestedIdentity === currentIdentityRef.current;
    } catch (error) {
      const handled = await session.handleRevisionConflict(error, {
        identity: requestedIdentity,
        planId: currentPlan.id,
        shopId: currentPlan.shopId,
      });
      if (!handled) toast.error(await getApiErrorMessage(error));
      return false;
    }
  };

  const applyOptimisticReviewChanges = (
    changes: SmartCreationReviewEditChange[],
  ): Promise<boolean> => {
    if (!changes.every(isOptimisticReviewChange)) return applyReviewChanges(changes);
    return reviewQueueRef.current!.enqueue(changes);
  };

  const refreshPreviewReview = async () => {
    const requestedIdentity = currentIdentityRef.current;
    if (
      !(await reviewQueueRef.current!.flush()) ||
      !mountedRef.current ||
      requestedIdentity !== currentIdentityRef.current
    )
      return;
    if (!(await confirmPreviewRegeneration()) || requestedIdentity !== currentIdentityRef.current)
      return;
    const currentPlan = browserDraftRef.current;
    if (!currentPlan?.reviewWorkspace) return;
    const identity = beginAction();
    if (!identity) return;
    mutations.refreshPreviewReviewMutation.mutate({ identity, currentPlan });
  };

  const discardPendingSubmission = () => {
    const currentPlan = browserDraftRef.current;
    if (!currentPlan) return;
    const identity = beginAction({ blockContent: false });
    if (!identity) return;
    mutations.discardSubmissionMutation.mutate({ identity, currentPlan });
  };

  const confirmReview = async () => {
    const requestedIdentity = currentIdentityRef.current;
    if (
      !(await reviewQueueRef.current!.flush()) ||
      !mountedRef.current ||
      requestedIdentity !== currentIdentityRef.current
    )
      return;
    const currentPlan = browserDraftRef.current;
    if (currentPlan?.status !== "preview_ready" || !currentPlan.reviewWorkspace) return;
    const identity = beginAction();
    if (!identity) return;
    mutations.confirmMutation.mutate({ identity, currentPlan });
  };

  const backFromReview = () => {
    const requestedIdentity = currentIdentityRef.current;
    void reviewQueueRef.current!.flush().then((accepted) => {
      if (accepted && mountedRef.current && requestedIdentity === currentIdentityRef.current)
        goBack();
    });
  };

  const retryPreviewPreparation = async () => {
    if (!(await confirmPreviewRegeneration())) return;
    const currentPlan = browserDraftRef.current;
    const identity = currentPlan ? beginAction({ blockContent: false }) : null;
    if (identity && currentPlan)
      mutations.generatePreviewMutation.mutate({
        identity,
        currentPlan,
        nextDraft: currentPlan.draft,
        stepAlreadySaved: true,
      });
  };

  const applyStrategyOperations = async (
    operations: SmartCreationStrategyOperation[],
    expectedRevision: number,
  ): Promise<boolean> => {
    if (operations.length === 0 || !(await confirmPreviewRegeneration())) return false;
    const currentPlan = browserDraftRef.current;
    if (!currentPlan || (currentPlan.status !== "draft" && currentPlan.status !== "preview_ready"))
      return false;
    const identity = beginAction({ blockContent: false });
    if (!identity) return false;
    try {
      await mutations.strategyMutation.mutateAsync({
        identity,
        currentPlan,
        expectedRevision,
        operations,
      });
      return identity === currentIdentityRef.current;
    } catch {
      return false;
    }
  };

  const refreshStrategyRecommendation = async () => {
    if (!(await confirmPreviewRegeneration())) return;
    const currentPlan = browserDraftRef.current;
    if (!currentPlan || (currentPlan.status !== "draft" && currentPlan.status !== "preview_ready"))
      return;
    const identity = beginAction({ blockContent: false });
    if (!identity) return;
    mutations.strategyRecommendationMutation.mutate({ identity, currentPlan });
  };

  const acceptStrategyRecommendation = async (
    strategyKind: SmartCreationTargetingStrategyKind,
    expectedRevision: number,
  ): Promise<boolean> => {
    if (!(await confirmPreviewRegeneration())) return false;
    const currentPlan = browserDraftRef.current;
    if (
      !currentPlan?.strategyRecommendation ||
      (currentPlan.status !== "draft" && currentPlan.status !== "preview_ready")
    )
      return false;
    const identity = beginAction({ blockContent: false });
    if (!identity) return false;
    try {
      await mutations.acceptStrategyRecommendationMutation.mutateAsync({
        identity,
        currentPlan,
        expectedRevision,
        strategyKind,
      });
      return identity === currentIdentityRef.current;
    } catch {
      return false;
    }
  };

  const removeStrategyLane = (lane: SmartCreationStrategyLane, expectedRevision: number) => {
    if (lane === "automatic") return;
    void applyStrategyOperations(
      [
        {
          operation: "set_lane_enabled",
          strategyKind: lane.split("_")[0] as Exclude<
            SmartCreationTargetingStrategyKind,
            "automatic"
          >,
          lane,
          enabled: false,
        },
      ],
      expectedRevision,
    );
  };

  return {
    applyReviewChanges,
    applyOptimisticReviewChanges,
    refreshPreviewReview,
    discardPendingSubmission,
    confirmReview,
    backFromReview,
    retryPreviewPreparation,
    applyStrategyOperations,
    refreshStrategyRecommendation,
    acceptStrategyRecommendation,
    removeStrategyLane,
  };
}
